/**
 * Deal Creation Flow
 *
 * Simple, document-focused flow for creating deals:
 * 1. Choose creation method (Upload / Template / Meeting Bot)
 * 2. Upload documents or select template
 * 3. Add context via chat/prompts
 * 4. Create deal workspace
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsDemo } from '@/hooks/common/useIsDemo';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, Upload, FileText, Calendar, ArrowRight, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from 'src/utils/Functions';
import { DocumentSelector } from '@/components/upload';
import { DealPipeline, type PipelineStage } from '@/components/ui/deal-pipeline';
import { PATH_ROOT } from '@/routes/paths';
import { RecordingAgreementFlow } from './RecordingAgreementFlow';

type CreationMethod = 'upload' | 'template' | 'meeting' | null;

interface DealCreationFlowProps {
  onComplete?: (dealData: any) => void;
  onCancel?: () => void;
  embedded?: boolean;
  initialMethod?: CreationMethod;
  initialTemplateId?: string | null;
  onBackToTemplateLibrary?: () => void;
}

export const DealCreationFlow: React.FC<DealCreationFlowProps> = ({
  onComplete,
  onCancel,
  embedded = false,
  initialMethod = null,
  initialTemplateId = null,
  onBackToTemplateLibrary,
}) => {
  const navigate = useNavigate();
  const isDemo = useIsDemo();
  const [selectedMethod, setSelectedMethod] = useState<CreationMethod>(initialMethod);

  const handleCancel = () => {
    if (selectedMethod) {
      if (initialMethod) {
        if (onCancel) {
          onCancel();
        } else {
          navigate(isDemo ? PATH_ROOT.demo.dealPipeline : PATH_ROOT.main.dealPipeline);
        }
        return;
      }
      setSelectedMethod(null);
      return;
    }

    if (onCancel) {
      onCancel();
    } else {
      navigate(isDemo ? PATH_ROOT.demo.dealPipeline : PATH_ROOT.main.dealPipeline);
    }
  };

  return (
    <div className={cn(embedded ? "h-full min-h-0" : "min-h-screen", "bg-background flex flex-col")}>
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur">
        <div className="mx-auto px-4 py-4">
          <Button variant="ghost" onClick={handleCancel} className="gap-2">
            <ArrowLeft className="size-3.5" />
            {selectedMethod ? (initialMethod ? 'Cancel' : 'Back') : 'Cancel'}
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className={cn("flex-1 flex justify-center p-6", selectedMethod === 'template' ? 'items-start pt-6' : 'items-center')}>
        <div className={cn("w-full", selectedMethod === 'template' ? 'max-w-7xl' : 'max-w-5xl')}>
          <AnimatePresence mode="wait" initial={false}>
            {!selectedMethod ? (
              <MethodSelection onSelect={setSelectedMethod} />
            ) : selectedMethod === 'upload' ? (
              <UploadFlow onComplete={onComplete} />
            ) : selectedMethod === 'template' ? (
              <TemplateFlow
                onComplete={onComplete}
                initialTemplateId={initialTemplateId}
                onBackToTemplateLibrary={onBackToTemplateLibrary}
              />
            ) : selectedMethod === 'meeting' ? (
              <MeetingBotFlow onComplete={onComplete} />
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Method Selection Screen
// ============================================================================

interface MethodSelectionProps {
  onSelect: (method: CreationMethod) => void;
}

const MethodSelection: React.FC<MethodSelectionProps> = ({ onSelect }) => {
  const methods = [
    {
      id: 'upload' as const,
      icon: Upload,
      title: 'Upload Documents',
      description: 'Have contracts or agreements? Upload them and I\'ll help you analyze and organize everything.',
      badge: 'Most Common',
      color: 'from-blue-500/20 to-blue-600/20',
      borderColor: 'border-blue-500/30',
      iconColor: 'text-blue-400',
    },
    {
      id: 'template' as const,
      icon: FileText,
      title: 'Start from Template',
      description: 'Create a new contract from our library of industry-standard templates.',
      badge: 'Quick Start',
      color: 'from-purple-500/20 to-purple-600/20',
      borderColor: 'border-purple-500/30',
      iconColor: 'text-purple-400',
    },
    {
      id: 'meeting' as const,
      icon: Calendar,
      title: 'Meeting Bot',
      description: 'Had a negotiation call? Our AI can join meetings and extract deal terms automatically.',
      badge: 'Smart',
      color: 'from-green-500/20 to-green-600/20',
      borderColor: 'border-green-500/30',
      iconColor: 'text-green-400',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2 }}
    >
      {/* Header */}
      <div className="text-center mb-12">
        <div className="mb-4 flex justify-center">
          <div className="p-4 bg-white/5 rounded-2xl border border-white/10">
            <Sparkles className="size-8 text-white/80" />
          </div>
        </div>
        <h1 className="text-4xl font-bold text-white mb-3">Create a New Deal</h1>
        <p className="text-white/60 text-lg">Choose how you'd like to get started</p>
      </div>

      {/* Method Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {methods.map((method, index) => (
          <button
            key={method.id}
            onClick={() => onSelect(method.id)}
            className={cn(
              'group relative p-8 rounded-3xl text-left',
              'bg-accent border border-transparent hover:border-border',
              method.color,
              'cursor-pointer',
              method.id === 'upload' && 'deal-creation-upload',
              method.id === 'template' && 'deal-creation-template',
              method.id === 'meeting' && 'deal-creation-meeting',
            )}
          >
            {/* Badge */}
            {method.badge && (
              <div className="absolute top-4 right-4">
                <span className="px-2 py-1 rounded-full bg-white/10 text-white/70 text-xs font-medium">
                  {method.badge}
                </span>
              </div>
            )}

            {/* Icon */}
            <div className="mb-4">
              <method.icon className={cn('size-8', method.iconColor)} />
            </div>

            {/* Title */}
            <h3 className="text-xl font-semibold text-white mb-2">{method.title}</h3>

            {/* Description */}
            <p className="text-white/60 text-sm leading-relaxed mb-4">
              {method.description}
            </p>

            {/* Arrow indicator */}
            <Button variant="secondary" className="flex items-center text-white/40 group-hover:text-white/70 transition-colors">
              <span className="text-sm font-medium">Get started</span>
              <ArrowRight className="size-4 ml-2 transition-transform" />
            </Button>
          </button>
        ))}
      </div>

      {/* Help text */}
      <div className="mt-8 text-center">
        <p className="text-white/40 text-sm">
          Not sure which to choose?{' '}
          <button className="text-white/60 hover:text-white underline">
            See examples
          </button>
        </p>
      </div>
    </motion.div>
  );
};

// ============================================================================
// Upload Flow
// ============================================================================

interface FlowProps {
  onComplete?: (data: any) => void;
}

const UploadFlow: React.FC<FlowProps> = ({ onComplete }) => {
  // Core documents
  const [coreFiles, setCoreFiles] = useState<File[]>([]);
  const [coreContracts, setCoreContracts] = useState<string[]>([]);

  // Additional documents
  const [additionalFiles, setAdditionalFiles] = useState<File[]>([]);
  const [additionalContracts, setAdditionalContracts] = useState<string[]>([]);
  const [showAdditional, setShowAdditional] = useState(false);

  // Deal stage
  const [pipelineStages, setPipelineStages] = useState<PipelineStage[]>([
    { id: 'drafting', label: 'Drafting', completed: false, current: true },
    { id: 'signing', label: 'Signing', completed: false },
    { id: 'delivery', label: 'Delivery', completed: false },
  ]);

  const [context, setContext] = useState('');

  const handleStageSelect = (stageId: string) => {
    setPipelineStages((stages) =>
      stages.map((stage) => ({
        ...stage,
        current: stage.id === stageId,
      }))
    );
  };

  const handleCreateDeal = () => {
    const currentStage = pipelineStages.find((s) => s.current)?.id || 'drafting';
    if (onComplete) {
      onComplete({
        coreFiles,
        coreContracts,
        additionalFiles,
        additionalContracts,
        context,
        stage: currentStage,
        method: 'upload',
      });
    }
  };

  const totalCoreItems = coreFiles.length + coreContracts.length;
  const totalAdditionalItems = additionalFiles.length + additionalContracts.length;
  const totalItems = totalCoreItems + totalAdditionalItems;

  // Auto-expand additional documents if items are added
  React.useEffect(() => {
    if (totalAdditionalItems > 0 && !showAdditional) {
      setShowAdditional(true);
    }
  }, [totalAdditionalItems, showAdditional]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      className="max-w-4xl mx-auto"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">Add Your Documents</h2>
        <p className="text-white/60">
          Upload the main deal documents and any additional context files
        </p>
      </div>

      {/* Core Documents */}
      <div className="mb-8" data-tour="deal-creation-upload">
        <div className="mb-4">
          <h3 className="text-white font-semibold text-lg mb-1">Core Document(s)</h3>
          <p className="text-white/50 text-sm">
            The main contracts or agreements for this deal
          </p>
        </div>
        <DocumentSelector
          files={coreFiles}
          selectedContracts={coreContracts}
          onFilesChange={setCoreFiles}
          onContractsChange={setCoreContracts}
        />
      </div>

      {/* Additional Documents - Collapsible */}
      <div className="mb-8">
        <button
          onClick={() => setShowAdditional(!showAdditional)}
          className="w-full flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/8 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center size-10 rounded-lg bg-white/10">
              <FileText className="size-5 text-white/50" />
            </div>
            <div className="text-left">
              <h3 className="text-white font-semibold">
                Additional Documents {totalAdditionalItems > 0 && `(${totalAdditionalItems})`}
              </h3>
              <p className="text-white/50 text-sm">
                Supporting materials, NDAs, previous agreements
              </p>
            </div>
          </div>
          {showAdditional ? (
            <ChevronUp className="size-5 text-white/40" />
          ) : (
            <ChevronDown className="size-5 text-white/40" />
          )}
        </button>

        <AnimatePresence>
          {showAdditional && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="mt-4">
                <DocumentSelector
                  files={additionalFiles}
                  selectedContracts={additionalContracts}
                  onFilesChange={setAdditionalFiles}
                  onContractsChange={setAdditionalContracts}
                  compact
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Deal Stage Selector */}
      <div className="mb-8">
        <label className="block text-white font-medium mb-3">
          Current deal stage
        </label>
        <DealPipeline
          stages={pipelineStages}
          onStageClick={handleStageSelect}
          variant="selector"
        />
        <p className="text-white/40 text-xs mt-2">
          Select the current stage of this deal
        </p>
      </div>

      {/* Context Input */}
      <div className="mb-8">
        <label className="block text-white font-medium mb-3">
          Tell me about this deal <span className="text-white/40 font-normal">(optional)</span>
        </label>
        <textarea
          value={context}
          onChange={(e) => setContext(e.target.value)}
          placeholder="e.g., This is a recording deal with Sony Music. $150K advance, need to review the recoupment terms and territory rights..."
          className="w-full h-32 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 resize-none focus:outline-none focus:border-white/30 transition-colors"
        />
        <p className="text-white/40 text-xs mt-2">
          Add any context that will help me understand the deal better
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4">
        <p className="text-white/50 text-sm">
          {totalCoreItems === 0 ? (
            'Add at least one core document to continue'
          ) : (
            <>
              {totalCoreItems} core doc{totalCoreItems === 1 ? '' : 's'}
              {totalAdditionalItems > 0 && ` + ${totalAdditionalItems} additional`}
            </>
          )}
        </p>
        <Button
          onClick={handleCreateDeal}
          disabled={totalCoreItems === 0}
          className="gap-2"
        >
          <Sparkles className="size-4" />
          Create Deal
          <ArrowRight className="size-4" />
        </Button>
      </div>
    </motion.div>
  );
};

// ============================================================================
// Template Flow
// ============================================================================

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  popular?: boolean;
}

const TemplateFlow: React.FC<
  FlowProps & {
    initialTemplateId?: string | null;
    onBackToTemplateLibrary?: () => void;
  }
> = ({ onComplete, initialTemplateId, onBackToTemplateLibrary }) => {
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);

  const useAutomatedFlow = Boolean(initialTemplateId) || selectedTemplate?.id === 'recording-deal';

  const handleAutomatedBack = () => {
    if (initialTemplateId && onBackToTemplateLibrary) {
      onBackToTemplateLibrary();
      return;
    }
    setSelectedTemplate(null);
  };

  const templates: Template[] = [
    {
      id: 'recording-deal',
      name: 'Recording Agreement',
      description: 'Standard recording contract covering advances, royalties, recoupment terms, and rights.',
      category: 'Recording',
      icon: '🎤',
      popular: true,
    },
    {
      id: 'sync-license',
      name: 'Sync License',
      description: 'License for use of music in film, TV, commercials, or video games.',
      category: 'Licensing',
      icon: '🎬',
      popular: true,
    },
    {
      id: 'touring-agreement',
      name: 'Touring Agreement',
      description: 'Contract for live performances covering fees, expenses, and technical requirements.',
      category: 'Live Performance',
      icon: '🎸',
    },
    {
      id: 'publishing-deal',
      name: 'Publishing Agreement',
      description: 'Music publishing contract for songwriting and composition rights.',
      category: 'Publishing',
      icon: '📝',
    },
    {
      id: 'production-agreement',
      name: 'Production Agreement',
      description: 'Contract between artist and producer covering beats, production work, and splits.',
      category: 'Production',
      icon: '🎹',
    },
    {
      id: 'management-contract',
      name: 'Management Contract',
      description: 'Artist management agreement covering commission, duration, and responsibilities.',
      category: 'Management',
      icon: '💼',
    },
    {
      id: 'distribution-deal',
      name: 'Distribution Agreement',
      description: 'Contract for digital and physical distribution of music releases.',
      category: 'Distribution',
      icon: '📦',
    },
    {
      id: 'collaboration',
      name: 'Collaboration Agreement',
      description: 'Split sheet for co-writers and collaborators to define ownership percentages.',
      category: 'Collaboration',
      icon: '🤝',
    },
  ];

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      className="max-w-6xl mx-auto"
    >
      {!useAutomatedFlow ? (
        <>
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">Choose a Template</h2>
            <p className="text-white/60">
              Start with an industry-standard contract template and customize it for your deal
            </p>
          </div>

          {/* Template Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-tour="deal-creation-template">
            {templates.map((template, index) => (
              <button
                key={template.id}
                onClick={() => template.id === 'recording-deal' && handleTemplateSelect(template)}
                disabled={template.id !== 'recording-deal'}
                className={cn(
                  'group relative p-6 rounded-xl border-2 text-left transition-all',
                  template.id === 'recording-deal'
                    ? 'bg-white/5 backdrop-blur border-white/10 hover:scale-105 hover:bg-white/8 hover:border-white/30 hover:shadow-xl cursor-pointer'
                    : 'bg-white/[0.02] backdrop-blur border-white/5 opacity-40 cursor-not-allowed'
                )}
              >
                {/* Popular Badge */}
                {template.popular && template.id === 'recording-deal' && (
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 text-xs font-medium border border-blue-500/30">
                      Popular
                    </span>
                  </div>
                )}

                {/* Coming Soon Badge */}
                {template.id !== 'recording-deal' && (
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 rounded-full bg-white/10 text-white/40 text-xs font-medium border border-white/10">
                      Coming Soon
                    </span>
                  </div>
                )}

                {/* Icon */}
                <div className="text-4xl mb-3">{template.icon}</div>

                {/* Title */}
                <h3 className={cn("text-lg font-semibold mb-2", template.id === 'recording-deal' ? 'text-white' : 'text-white/40')}>{template.name}</h3>

                {/* Category */}
                <div className="mb-2">
                  <span className="text-xs text-white/40 uppercase tracking-wider">
                    {template.category}
                  </span>
                </div>

                {/* Description */}
                <p className={cn("text-sm leading-relaxed", template.id === 'recording-deal' ? 'text-white/60' : 'text-white/30')}>
                  {template.description}
                </p>

                {/* Arrow indicator */}
                {template.id === 'recording-deal' && (
                  <div className="mt-4 flex items-center text-white/40 group-hover:text-white/70 transition-colors">
                    <span className="text-sm font-medium">Select</span>
                    <ArrowRight className="size-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </>
      ) : (
        <RecordingAgreementFlow
          onComplete={onComplete}
          onBack={handleAutomatedBack}
        />
      )}
    </motion.div>
  );
};

// ============================================================================
// Meeting Bot Flow
// ============================================================================

type CalendarProvider = 'google' | 'outlook' | 'manual' | null;

interface MeetingDetails {
  title: string;
  date: string;
  time: string;
  duration: string;
  meetingLink: string;
}

const MeetingBotFlow: React.FC<FlowProps> = ({ onComplete }) => {
  const [step, setStep] = useState<'provider' | 'meeting' | 'config'>('provider');
  const [selectedProvider, setSelectedProvider] = useState<CalendarProvider>(null);
  const [meetingDetails, setMeetingDetails] = useState<MeetingDetails>({
    title: '',
    date: '',
    time: '',
    duration: '60',
    meetingLink: '',
  });
  const [focusAreas, setFocusAreas] = useState<string[]>(['terms', 'pricing']);
  const [context, setContext] = useState('');

  const calendarProviders = [
    {
      id: 'google' as const,
      name: 'Google Calendar',
      icon: '📅',
      description: 'Connect your Google Calendar to select upcoming meetings',
    },
    {
      id: 'outlook' as const,
      name: 'Outlook Calendar',
      icon: '📆',
      description: 'Connect your Outlook Calendar to select upcoming meetings',
    },
    {
      id: 'manual' as const,
      name: 'Enter Manually',
      icon: '✏️',
      description: 'Manually enter meeting details and invite the bot',
    },
  ];

  const focusAreaOptions = [
    { id: 'terms', label: 'Key Terms & Conditions', icon: '📋' },
    { id: 'pricing', label: 'Pricing & Payment', icon: '💰' },
    { id: 'timeline', label: 'Timeline & Deadlines', icon: '⏰' },
    { id: 'rights', label: 'Rights & Territory', icon: '🌍' },
    { id: 'obligations', label: 'Obligations & Responsibilities', icon: '✓' },
    { id: 'disputes', label: 'Dispute Resolution', icon: '⚖️' },
  ];

  const handleProviderSelect = (provider: CalendarProvider) => {
    setSelectedProvider(provider);
    setStep('meeting');
  };

  const handleMeetingContinue = () => {
    setStep('config');
  };

  const toggleFocusArea = (areaId: string) => {
    setFocusAreas(prev =>
      prev.includes(areaId)
        ? prev.filter(id => id !== areaId)
        : [...prev, areaId]
    );
  };

  const handleCreateDeal = () => {
    if (onComplete) {
      onComplete({
        provider: selectedProvider,
        meeting: meetingDetails,
        focusAreas,
        context,
        method: 'meeting',
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      className="max-w-4xl mx-auto"
    >
      <AnimatePresence mode="wait" initial={false}>
        {/* Step 1: Calendar Provider Selection */}
        {step === 'provider' && (
          <motion.div
            key="provider"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.15 }}
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">Connect Your Calendar</h2>
              <p className="text-white/60">
                Choose how you'd like to add the AI bot to your negotiation call
              </p>
            </div>

            <div className="space-y-4">
              {calendarProviders.map((provider, index) => (
                <button
                  key={provider.id}
                  onClick={() => handleProviderSelect(provider.id)}
                  className={cn(
                    'w-full group relative p-6 rounded-xl border-2 text-left transition-all',
                    'bg-white/5 backdrop-blur border-white/10',
                    'hover:scale-[1.02] hover:bg-white/8 hover:border-white/30 hover:shadow-xl cursor-pointer',
                    'flex items-center gap-4'
                  )}
                >
                  <div className="text-4xl">{provider.icon}</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-1">{provider.name}</h3>
                    <p className="text-white/60 text-sm">{provider.description}</p>
                  </div>
                  <ArrowRight className="size-5 text-white/40 group-hover:text-white/70 group-hover:translate-x-1 transition-all" />
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Step 2: Meeting Details */}
        {step === 'meeting' && (
          <motion.div
            key="meeting"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.15 }}
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">Meeting Details</h2>
              <p className="text-white/60">
                Enter the details for the call you want the bot to join
              </p>
            </div>

            <div className="space-y-6">
              {/* Meeting Title */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Meeting Title
                </label>
                <input
                  type="text"
                  value={meetingDetails.title}
                  onChange={(e) => setMeetingDetails(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Recording Deal Negotiation with Sony Music"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-white/30 transition-colors"
                />
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    value={meetingDetails.date}
                    onChange={(e) => setMeetingDetails(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-white/30 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-white font-medium mb-2">
                    Time
                  </label>
                  <input
                    type="time"
                    value={meetingDetails.time}
                    onChange={(e) => setMeetingDetails(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-white/30 transition-colors"
                  />
                </div>
              </div>

              {/* Duration */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Duration (minutes)
                </label>
                <select
                  value={meetingDetails.duration}
                  onChange={(e) => setMeetingDetails(prev => ({ ...prev, duration: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-white/30 transition-colors"
                >
                  <option value="30">30 minutes</option>
                  <option value="60">1 hour</option>
                  <option value="90">1.5 hours</option>
                  <option value="120">2 hours</option>
                </select>
              </div>

              {/* Meeting Link */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Meeting Link
                </label>
                <input
                  type="url"
                  value={meetingDetails.meetingLink}
                  onChange={(e) => setMeetingDetails(prev => ({ ...prev, meetingLink: e.target.value }))}
                  placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-white/30 transition-colors"
                />
                <p className="text-white/40 text-xs mt-2">
                  The bot will join this link at the scheduled time
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-6">
              <Button
                variant="ghost"
                onClick={() => setStep('provider')}
              >
                Back
              </Button>
              <Button
                onClick={handleMeetingContinue}
                disabled={!meetingDetails.title || !meetingDetails.date || !meetingDetails.time || !meetingDetails.meetingLink}
                className="gap-2"
              >
                Continue
                <ArrowRight className="size-4" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Bot Configuration */}
        {step === 'config' && (
          <motion.div
            key="config"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.15 }}
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">Configure Your Bot</h2>
              <p className="text-white/60">
                Tell the bot what to listen for during your call
              </p>
            </div>

            {/* Focus Areas */}
            <div className="mb-8">
              <h4 className="text-white font-medium mb-4">
                What should the bot focus on?
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {focusAreaOptions.map((area) => (
                  <button
                    key={area.id}
                    onClick={() => toggleFocusArea(area.id)}
                    className={cn(
                      'p-4 rounded-xl border-2 text-left transition-all',
                      'flex items-center gap-3',
                      focusAreas.includes(area.id)
                        ? 'bg-white/10 border-white/30 shadow-lg'
                        : 'bg-white/5 border-white/10 hover:bg-white/8 hover:border-white/20'
                    )}
                  >
                    <div className="text-2xl">{area.icon}</div>
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">{area.label}</p>
                    </div>
                    {focusAreas.includes(area.id) && (
                      <div className="size-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">✓</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Context */}
            <div className="mb-8">
              <label className="block text-white font-medium mb-3">
                Additional context <span className="text-white/40 font-normal">(optional)</span>
              </label>
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="e.g., This is a follow-up call after initial discussions. We've agreed on $100K advance in principle, but need to finalize the recoupment structure and territory rights. Key contact is Sarah from their A&R team..."
                className="w-full h-32 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 resize-none focus:outline-none focus:border-white/30 transition-colors"
              />
              <p className="text-white/40 text-xs mt-2">
                Help the bot understand what to expect from this call
              </p>
            </div>

            {/* Summary Card */}
            <div className="mb-6 p-6 bg-white/5 rounded-xl border border-white/10">
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <Calendar className="size-5" />
                Meeting Summary
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/60">Title:</span>
                  <span className="text-white">{meetingDetails.title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Date:</span>
                  <span className="text-white">{meetingDetails.date} at {meetingDetails.time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Duration:</span>
                  <span className="text-white">{meetingDetails.duration} minutes</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Focus Areas:</span>
                  <span className="text-white">{focusAreas.length} selected</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4">
              <Button
                variant="ghost"
                onClick={() => setStep('meeting')}
              >
                Back
              </Button>
              <Button
                onClick={handleCreateDeal}
                className="gap-2"
              >
                <Sparkles className="size-4" />
                Schedule Bot & Create Deal
                <ArrowRight className="size-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default DealCreationFlow;
